<template>
    <app-modal-new
        name="sharepoint-reconnect-popup"
        title="INTEGRATIONS.SHAREPOINT.RECONNECT_POPUP_TITLE"
        :confirm-action-label="confirmActionLabel"
        :hide-cancel-action="true"
        @handle-confirm="handleConfirm"
        @on-close="markShown"
    >
        <template #content>
            <p class="sharepoint-reconnect-popup__body">
                {{ $t(bodyTranslationKey) }}
            </p>
        </template>
    </app-modal-new>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import {
    COMPANY_ROLES,
    ROUTES_COMPANY_SETTINGS
} from '@src/modules/company-settings/constants'
import { service } from './api'
import { LOCAL_STORAGE_ITEMS } from '@src/constants/global'
import AppModalNew from '@src/components/AppModalNew'

export default {
    components: { AppModalNew },

    computed: {
        ...mapState('company', ['company']),
        ...mapState('integrations', ['sharepoint']),
        ...mapGetters('account', ['isLoggedIn', 'companyMember', 'user']),
        ...mapGetters('company', ['isOnboardingCompleted']),
        ...mapGetters('integrations', ['shouldShowSharepointReconnectPopup']),

        // Per user+company localStorage key for "popup already shown". Null until both ids
        // are known (pre-login / company not yet loaded) — callers must guard on null.
        popupStorageKey () {
            const userId = this.user?.id
            const companyId = this.company?.id
            if (!userId || !companyId) {
                return null
            }
            return `${LOCAL_STORAGE_ITEMS.SHAREPOINT_RECONNECT_POPUP_SHOWN_PREFIX}${userId}_${companyId}`
        },

        isOnSharepointPage () {
            return (
                this.$route.name ===
                ROUTES_COMPANY_SETTINGS.INTEGRATIONS_SHAREPOINT.name
            )
        },

        // Members lack permission to manage integrations (the SharePoint settings route is
        // admin-only and 403s for them). For Members the popup becomes an info notice — they
        // see a "contact your admin" body and a single dismiss button instead of the reconnect
        // CTA that would route them to a forbidden page they cannot recover from.
        isMember () {
            return (
                this.companyMember?.role?.name === COMPANY_ROLES.MEMBER
            )
        },

        bodyTranslationKey () {
            return this.isMember
                ? 'INTEGRATIONS.SHAREPOINT.RECONNECT_POPUP_BODY_MEMBER'
                : 'INTEGRATIONS.SHAREPOINT.RECONNECT_POPUP_BODY'
        },

        confirmActionLabel () {
            return this.isMember
                ? 'COMMON.OK'
                : 'INTEGRATIONS.SHAREPOINT.RECONNECT_POPUP_CTA'
        }
    },

    watch: {
        'company.id': {
            immediate: true,
            handler (id, oldId) {
                if (!id || !this.isLoggedIn) {
                    return
                }
                // Fresh login or company switch — clear last session's
                // "already shown" flag and stale status so the popup can
                // re-evaluate against the new company's data.
                if (id !== oldId) {
                    this.resetSharepointReconnectPopupSession()
                }
                this.fetchStatus(id)
            }
        },

        shouldShowSharepointReconnectPopup: {
            immediate: true,
            handler (shouldShow) {
                if (shouldShow) {
                    this.maybeShow()
                }
            }
        },

        $route () {
            if (this.shouldShowSharepointReconnectPopup) {
                this.maybeShow()
            }
        }
    },

    methods: {
        ...mapMutations('integrations', [
            'setSharepointStatus',
            'setSharepointReconnectPopupShown',
            'resetSharepointReconnectPopupSession'
        ]),

        async fetchStatus (companyId) {
            // Bypass the store action on purpose: the canonical action toggles a full-screen loader and fires an error toast, neither appropriate for a silent app-boot probe.
            try {
                const response = await service.getSharepointStatus(companyId)
                // Bail if the user switched companies mid-flight; otherwise a slow response from the previous company would overwrite the reset state and mis-trigger the popup for the wrong company.
                if (this.company?.id !== companyId) {
                    return
                }
                this.setSharepointStatus(response)
            } catch (e) {
                // Silent: non-SP customers and transient BE errors must not surface from a background probe.
            }
        },

        maybeShow () {
            if (
                !this.isLoggedIn ||
                !this.isOnboardingCompleted ||
                this.isOnSharepointPage
            ) {
                return
            }
            // Already shown earlier this login session (persisted across hard refreshes).
            // Sync the in-memory flag so the getter stops re-triggering, and do not re-show.
            if (
                this.popupStorageKey &&
                localStorage.getItem(this.popupStorageKey) === '1'
            ) {
                this.setSharepointReconnectPopupShown()
                return
            }
            this.$modal.show('sharepoint-reconnect-popup')
        },

        markShown () {
            this.setSharepointReconnectPopupShown()
            // Persist so the popup stays suppressed across refreshes / extra tabs until the
            // next login clears it (account/doLogin). Logout is unreliable — sessions expire.
            if (this.popupStorageKey) {
                localStorage.setItem(this.popupStorageKey, '1')
            }
        },

        handleConfirm () {
            this.markShown()
            this.$modal.hide('sharepoint-reconnect-popup')
            // Members cannot navigate to the admin-only SharePoint settings route — their
            // "OK" button is a pure dismiss. Admins keep the reconnect-now navigation.
            if (this.isMember) {
                return
            }
            if (!this.isOnSharepointPage) {
                this.$router.push({
                    name:
                        ROUTES_COMPANY_SETTINGS.INTEGRATIONS_SHAREPOINT.name
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@style/sfc.scss';

.sharepoint-reconnect-popup {
    &__body {
        margin: 0;
    }
}
</style>
